import { requireKey } from '../api.js';
import { genConcept } from '../build/concept.js';
import { $, captureInspoScreenshot, esc, loadFont, mk, showErr } from '../dom.js';
import { formatBriefDownload, normalizeColors } from '../logic/brief-review.ts';
import { ST } from '../state.js';
import { analyseExisting } from './existing.js';
import { G } from './nav.js';
import { runDocAnalysis, runQAEnrichment } from './uploads.js';

// ── BRIEF REVIEW & FACT INVENTORY ─────────────────────────────────
// Marketing brief review screen, fact editing, color/tag management.
// Pure logic delegated to logic/brief-review.ts

/**
 * Download the marketing brief as a plain text file.
 * Uses formatBriefDownload() from logic/brief-review.ts for formatting.
 */
function downloadBrief() {
  const mb = ST.marketingBrief;
  if (!mb) {
    showErr('No brief data to download.');
    return;
  }
  const text = formatBriefDownload(mb, ST.existingUrl);
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  Object.assign(document.createElement('a'), { href: url, download: 'business-dna.txt' }).click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * Build an array of fact category objects from extracted facts state.
 * @returns {Array<{key: string, label: string, values: string[]}>}
 */
function getFactCategories() {
  const f = ST.extractedFacts || {};
  return [
    { key: 'businessName', label: 'Business name', values: f.businessName ? [f.businessName] : [] },
    { key: 'taglines', label: 'Taglines', values: f.taglines ? [].concat(f.taglines) : [] },
    { key: 'phone', label: 'Phone', values: f.phone ? [].concat(f.phone) : [] },
    { key: 'area', label: 'Service area', values: f.area ? [f.area] : [] },
    { key: 'services', label: 'Services', values: f.services ? [].concat(f.services) : [] },
    {
      key: 'credentials',
      label: 'Credentials',
      values: f.credentials ? [].concat(f.credentials) : [],
    },
    { key: 'namedStaff', label: 'People', values: f.namedStaff ? [].concat(f.namedStaff) : [] },
    { key: 'guarantees', label: 'Guarantees', values: f.guarantees ? [].concat(f.guarantees) : [] },
    { key: 'ctaTexts', label: 'CTA buttons', values: f.ctaTexts ? [].concat(f.ctaTexts) : [] },
  ];
}

/**
 * Write fact values back to state.
 * @param {string} key - Fact category key
 * @param {string[]} vals - Updated values
 */
function setFactValues(key, vals) {
  const f = ST.extractedFacts;
  if (!f) {
    return;
  }
  if (key === 'businessName') {
    f.businessName = vals[0] || '';
  } else if (key === 'area') {
    f.area = vals[0] || '';
  } else {
    f[key] = vals;
  }
}

/** Render the fact inventory with editable chips, add/remove, and confirm button. */
function renderFactInventory() {
  const c = $('ex-fact-inventory');
  if (!c) {
    return;
  }
  c.innerHTML = '';
  const cats = getFactCategories();
  const banner = mk('div', { class: 'fact-inv-banner' });
  banner.textContent =
    'Review these details — they will be preserved verbatim in your new site. Click any fact to edit, or add missing information.';
  c.append(banner);
  const inv = mk('div', { class: 'fact-inv' });
  cats.forEach(cat => {
    if (
      cat.values.length === 0 &&
      ['taglines', 'guarantees', 'namedStaff', 'ctaTexts'].indexOf(cat.key) >= 0
    ) {
      return;
    }
    const catDiv = mk('div', { class: 'fact-cat' });
    const hdr = mk('div', { class: 'fact-cat-hdr' });
    hdr.append(
      mk('span', { class: 'fact-cat-label' }, cat.label),
      mk(
        'button',
        {
          class: 'fact-cat-add',
          onclick(e) {
            e.stopPropagation();
            addFactToCategory(cat.key);
          },
        },
        '+ Add',
      ),
    );
    catDiv.append(hdr);
    const chips = mk('div', { class: 'fact-chips' });
    if (cat.values.length === 0) {
      chips.append(mk('span', { class: 'fact-empty' }, 'None found'));
    }
    cat.values.forEach((val, idx) => {
      const isEd = ST.editingFact && ST.editingFact.cat === cat.key && ST.editingFact.idx === idx;
      if (isEd) {
        const wrap = mk('div', { class: 'fact-chip-edit' });
        const inp = mk('input', { class: 'fact-chip-inp', type: 'text', value: val });
        inp.addEventListener('keydown', e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            saveFactEdit(cat.key, idx, inp.value);
          }
          if (e.key === 'Escape') {
            ST.editingFact = null;
            renderFactInventory();
          }
        });
        wrap.append(
          inp,
          mk(
            'button',
            {
              class: 'fact-chip-save',
              onclick() {
                saveFactEdit(cat.key, idx, inp.value);
              },
            },
            'OK',
          ),
          mk(
            'button',
            {
              class: 'fact-chip-cancel',
              onclick() {
                ST.editingFact = null;
                renderFactInventory();
              },
            },
            'x',
          ),
        );
        chips.append(wrap);
        setTimeout(() => {
          inp.focus();
          inp.select();
        }, 30);
      } else {
        const chip = mk('div', { class: 'fact-chip' });
        chip.append(
          mk('span', { style: 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap' }, val),
          mk(
            'span',
            {
              class: 'fact-chip-x',
              onclick(e) {
                e.stopPropagation();
                removeFactFromCategory(cat.key, idx);
              },
            },
            'x',
          ),
        );
        chip.addEventListener('click', () => {
          ST.editingFact = { cat: cat.key, idx };
          renderFactInventory();
        });
        chips.append(chip);
      }
    });
    catDiv.append(chips);
    inv.append(catDiv);
  });
  const cr = mk('div', { class: 'fact-confirm-row' });
  if (ST.factsConfirmed) {
    cr.append(
      mk('div', { class: 'fact-confirmed-badge' }, 'Facts confirmed'),
      mk(
        'button',
        {
          class: 'fact-confirmed-edit',
          onclick() {
            ST.factsConfirmed = false;
            renderFactInventory();
            updateExOptsGate();
          },
        },
        'Edit again',
      ),
    );
  } else {
    cr.append(
      mk(
        'button',
        {
          class: 'fact-confirm-btn fact-confirm-go',
          onclick() {
            ST.factsConfirmed = true;
            ST.editingFact = null;
            renderFactInventory();
            updateExOptsGate();
            const gate = $('ex-opts-gate');
            if (gate) {
              setTimeout(() => gate.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 80);
            }
          },
        },
        'Facts look good →',
      ),
      mk('span', { style: 'font-size:12px;color:var(--txt3)' }, 'Confirm before choosing'),
    );
  }
  inv.append(cr);
  c.append(inv);
}

function updateExOptsGate() {
  const gate = $('ex-opts-gate');
  if (gate) {
    gate.style.display = ST.factsConfirmed ? 'block' : 'none';
  }
}

function saveFactEdit(catKey, idx, newVal) {
  const v = newVal.trim();
  if (!v) {
    removeFactFromCategory(catKey, idx);
    return;
  }
  const cats = getFactCategories();
  const cat = cats.find(c => c.key === catKey);
  if (!cat) {
    return;
  }
  cat.values[idx] = v;
  setFactValues(catKey, cat.values);
  ST.editingFact = null;
  if (ST.factsConfirmed) {
    ST.factsConfirmed = false;
    updateExOptsGate();
  }
  renderFactInventory();
}

function removeFactFromCategory(catKey, idx) {
  const cats = getFactCategories();
  const cat = cats.find(c => c.key === catKey);
  if (!cat) {
    return;
  }
  cat.values.splice(idx, 1);
  setFactValues(catKey, cat.values);
  ST.editingFact = null;
  if (ST.factsConfirmed) {
    ST.factsConfirmed = false;
    updateExOptsGate();
  }
  renderFactInventory();
}

function addFactToCategory(catKey) {
  const f = ST.extractedFacts;
  if (!f[catKey]) {
    f[catKey] = [];
  }
  if (catKey === 'businessName') {
    setFactValues(catKey, [f.businessName || '', '']);
  } else if (catKey === 'area') {
    setFactValues(catKey, [f.area || '', '']);
  } else {
    const arr = Array.isArray(f[catKey]) ? f[catKey] : [];
    arr.push('');
    f[catKey] = arr;
  }
  const vals = getFactCategories().find(c => c.key === catKey).values;
  ST.editingFact = { cat: catKey, idx: vals.length - 1 };
  if (ST.factsConfirmed) {
    ST.factsConfirmed = false;
    updateExOptsGate();
  }
  renderFactInventory();
}

// ── DOM RENDERING (becomes BriefReviewPage.vue during migration) ──
// renderBriefReview(), renderFactInventory(), _renderTagCard()

/**
 * Render the full brief review screen — identity, fonts, colors,
 * tagline, brand values, aesthetic, tone, overview, images, facts.
 */
function renderBriefReview() {
  const mb = ST.marketingBrief;
  if (!mb) {
    return;
  }

  // ── Identity card ──
  const idCard = $('br-identity');
  if (idCard) {
    let logoHtml = '';
    const logo = ST.logoImg?.preview || ST.logoUrl || mb.logoUrl || '';
    if (logo) {
      logoHtml = `<div class="br-logo-wrap"><img class="br-logo-preview" src="${esc(logo)}" alt="Logo" onerror="this.parentNode.style.display='none'"></div>`;
    }
    idCard.innerHTML = `<div class="br-card-hdr"><span class="br-card-label">Identity</span><span class="br-card-edit" onclick="G.brEdit('identity')">✎</span></div>
      <div style="display:flex;gap:14px;align-items:center">${logoHtml}<div>
        <div class="br-identity-name" id="br-id-name">${esc(mb.companyName || '')}</div>
        ${mb.sourceUrl ? `<div class="br-identity-url">${esc(mb.sourceUrl)}</div>` : ''}
        <div class="br-identity-industry" id="br-id-industry">${esc(mb.industry || '')}</div>
      </div></div>`;
  }

  // ── Fonts card ──
  const fontsCard = $('br-fonts');
  if (fontsCard) {
    const hf = mb.fonts?.heading?.family || '';
    const bf = mb.fonts?.body?.family || '';
    if (hf) {
      loadFont(hf, bf || hf);
    }
    const sampleName = mb.companyName || 'Your Brand';
    fontsCard.innerHTML = `<div class="br-card-hdr"><span class="br-card-label">Typography</span><span class="br-card-edit" onclick="G.brEdit('fonts')">✎</span></div>
      <div class="br-font-row"><div class="br-font-sample" style="font-family:'${esc(hf)}',sans-serif">${esc(sampleName)}</div><div><div style="font-weight:600;font-size:13px">${esc(
        hf || 'Not detected',
      )}</div><div class="br-font-meta">Heading · ${esc(mb.fonts?.heading?.style || '')}</div></div></div>
      <div class="br-font-row"><div class="br-font-sample br-font-body" style="font-family:'${esc(bf)}',sans-serif">The quick brown fox jumps over the lazy dog.</div><div><div style="font-weight:600;font-size:13px">${esc(
        bf || 'Not detected',
      )}</div><div class="br-font-meta">Body · ${esc(mb.fonts?.body?.style || '')}</div></div></div>`;
  }

  // ── Colors card ──
  const colorsCard = $('br-colors');
  if (colorsCard) {
    // Normalize colors — AI may return {hex}, {value}, or plain strings
    if (mb.colors?.length) {
      mb.colors = normalizeColors(mb.colors);
    }
    const swatches = (mb.colors || [])
      .map(
        (c, i) =>
          `<div class="br-swatch-wrap" onclick="G.brEditColor(${i})"><div class="br-swatch" style="background:${esc(c.hex)}" title="${esc(c.name)}"></div><span class="br-swatch-role">${esc(
            c.role || '',
          )}</span></div>`,
      )
      .join('');
    colorsCard.innerHTML = `<div class="br-card-hdr"><span class="br-card-label">Colors (${(mb.colors || []).length})</span></div>
      <div class="br-color-row">${swatches}<div class="br-swatch-wrap"><div class="br-swatch-add" onclick="G.brAddColor()">+</div></div></div>`;
  }

  // ── Tagline card ──
  const tagCard = $('br-tagline');
  if (tagCard) {
    tagCard.innerHTML = `<div class="br-card-hdr"><span class="br-card-label">Tagline</span><span class="br-card-edit" onclick="G.brEdit('tagline')">✎</span></div>
      <div class="br-tagline" id="br-tag-text">${esc(mb.tagline || 'No tagline detected')}</div>`;
  }

  // ── Brand values card ──
  RenderTagCard('br-values', 'Brand Values', mb.brandValues || [], 'brandValues');

  // ── Visual aesthetic card ──
  RenderTagCard('br-aesthetic', 'Visual Aesthetic', mb.visualAesthetic || [], 'visualAesthetic');

  // ── Tone of voice card ──
  RenderTagCard('br-tone', 'Tone of Voice', mb.toneOfVoice || [], 'toneOfVoice');

  // ── Business overview card ──
  const overCard = $('br-overview');
  if (overCard) {
    overCard.innerHTML = `<div class="br-card-hdr"><span class="br-card-label">Business Overview</span><span class="br-card-edit" onclick="G.brEdit('overview')">✎</span></div>
      <div class="br-overview-text" id="br-over-text">${esc(mb.businessOverview || '')}</div>`;
  }

  // ── Images card ──
  const imgCard = $('br-images');
  if (imgCard) {
    const imgs = (mb.images || [])
      .map(
        (img, i) =>
          `<div class="br-img-wrap"><img src="${esc(img.src)}" alt="${esc(
            img.alt || '',
          )}" onerror="this.parentNode.style.display='none'"><div class="br-img-badge">${esc(
            img.category || '',
          )}</div><button class="br-img-del" onclick="G.brDelImage(${i})">×</button></div>`,
      )
      .join('');
    imgCard.innerHTML = `<div class="br-card-hdr"><span class="br-card-label">Images (${(mb.images || []).length})</span></div>
      <div class="br-img-grid">${imgs}<label class="br-img-upload"><input type="file" accept="image/*" multiple style="display:none" onchange="G.brAddImages(this.files)">+</label></div>`;
  }

  // ── Discovered pages card ──
  const pagesCard = $('br-pages');
  if (pagesCard) {
    if (ST.discoveredPages?.length) {
      let pagesHtml =
        '<div class="br-card-hdr"><span class="br-card-label">Pages found on your site</span></div>';
      pagesHtml += '<div class="br-pages-list">';
      pagesHtml +=
        '<label class="br-page-row br-page-home"><input type="checkbox" checked disabled><span class="br-page-name">Home</span><span class="br-page-path">/</span></label>';
      ST.discoveredPages.forEach((p, i) => {
        pagesHtml += `<label class="br-page-row"><input type="checkbox" ${p.selected ? 'checked' : ''} onchange="ST.discoveredPages[${i}].selected=this.checked"><span class="br-page-name">${esc(p.name)}</span><span class="br-page-path">${esc(p.path)}</span></label>`;
      });
      pagesHtml += '</div>';
      pagesHtml +=
        '<div class="br-pages-hint">Select pages to include in your new site. You can always add more later.</div>';
      pagesCard.innerHTML = pagesHtml;
    } else {
      pagesCard.innerHTML = '';
    }
  }

  // ── Facts section — hidden from UI, data stays in ST.marketingBrief for downstream prompts ──
  const factsEl = $('br-facts');
  if (factsEl) {
    factsEl.innerHTML = '';
  }

  // ── Conversion Intelligence — hidden from UI, data stays in ST.marketingBrief ──
  const convEl = $('br-conversion');
  if (convEl) {
    convEl.innerHTML = '';
  }
}

function RenderTagCard(elId, label, tags, field) {
  const el = $(elId);
  if (!el) {
    return;
  }
  const tagHtml = tags
    .map(
      (t, i) =>
        `<span class="br-tag">${esc(t)}<span class="br-tag-x" onclick="G.brRemoveTag('${field}',${i})">×</span></span>`,
    )
    .join('');
  el.innerHTML = `<div class="br-card-hdr"><span class="br-card-label">${label}</span></div>
    <div class="br-tags">${tagHtml}<span class="br-tag-add" onclick="G.brAddTag('${field}')">+ Add</span></div>`;
}

/**
 * Toggle inline editing for a brief review field.
 * @param {'identity'|'tagline'|'overview'|'fonts'} field
 */
function brEditField(field) {
  const mb = ST.marketingBrief;
  if (!mb) {
    return;
  }

  if (field === 'identity') {
    const nameEl = $('br-id-name');
    const indEl = $('br-id-industry');
    if (nameEl && !nameEl.querySelector('input')) {
      const curName = mb.companyName || '';
      const curInd = mb.industry || '';
      nameEl.innerHTML = `<input class="br-edit-inp" value="${esc(curName)}" onchange="ST.marketingBrief.companyName=this.value;ST.marketingBrief.userEdits=ST.marketingBrief.userEdits||{};ST.marketingBrief.userEdits.companyName=true" autofocus>`;
      if (indEl) {
        indEl.innerHTML = `<input class="br-edit-inp" value="${esc(curInd)}" placeholder="Industry" onchange="ST.marketingBrief.industry=this.value;ST.marketingBrief.userEdits=ST.marketingBrief.userEdits||{};ST.marketingBrief.userEdits.industry=true">`;
      }
    }
  } else if (field === 'tagline') {
    const el = $('br-tag-text');
    if (el && !el.querySelector('input')) {
      el.innerHTML = `<input class="br-edit-inp" value="${esc(
        mb.tagline || '',
      )}" onchange="ST.marketingBrief.tagline=this.value;ST.marketingBrief.userEdits=ST.marketingBrief.userEdits||{};ST.marketingBrief.userEdits.tagline=true" autofocus>`;
    }
  } else if (field === 'overview') {
    const el = $('br-over-text');
    if (el && !el.querySelector('textarea')) {
      el.innerHTML = `<textarea class="br-edit-ta" onchange="ST.marketingBrief.businessOverview=this.value;ST.marketingBrief.userEdits=ST.marketingBrief.userEdits||{};ST.marketingBrief.userEdits.businessOverview=true">${esc(
        mb.businessOverview || '',
      )}</textarea>`;
    }
  } else if (field === 'fonts') {
    // Ensure fonts structure exists before editing
    if (!mb.fonts) {
      mb.fonts = { heading: { family: '', style: '' }, body: { family: '', style: '' } };
    }
    if (!mb.fonts.heading) {
      mb.fonts.heading = { family: '', style: '' };
    }
    if (!mb.fonts.body) {
      mb.fonts.body = { family: '', style: '' };
    }
    const card = $('br-fonts');
    if (card) {
      card.innerHTML = `<div class="br-card-hdr"><span class="br-card-label">Typography</span></div>
        <div style="display:flex;flex-direction:column;gap:8px">
          <input class="br-edit-inp" placeholder="Heading font (Google Fonts)" value="${esc(mb.fonts.heading.family || '')}" onchange="ST.marketingBrief.fonts.heading.family=this.value;loadFont(this.value,ST.marketingBrief.fonts.body.family||this.value)">
          <input class="br-edit-inp" placeholder="Body font (Google Fonts)" value="${esc(mb.fonts.body.family || '')}" onchange="ST.marketingBrief.fonts.body.family=this.value;loadFont(ST.marketingBrief.fonts.heading.family||this.value,this.value)">
          <button class="btn btn-g btn-sm" onclick="renderBriefReview()" style="align-self:flex-end;margin-top:4px">Done</button>
        </div>`;
    }
  }
}

/** Remove a tag from a brief array field (brandValues, toneOfVoice, etc.). */
function brRemoveTag(field, idx) {
  const mb = ST.marketingBrief;
  if (!(mb && mb[field])) {
    return;
  }
  mb[field].splice(idx, 1);
  renderBriefReview();
}

/** Prompt user to add a tag to a brief array field. */
function brAddTag(field) {
  const tag = prompt('Add a tag:');
  if (!tag?.trim()) {
    return;
  }
  const mb = ST.marketingBrief;
  if (!mb) {
    return;
  }
  if (!mb[field]) {
    mb[field] = [];
  }
  mb[field].push(tag.trim());
  renderBriefReview();
}

/** Prompt user to edit a color hex value at the given index. */
function brEditColor(idx) {
  const mb = ST.marketingBrief;
  if (!mb?.colors?.[idx]) {
    return;
  }
  const hex = prompt('Edit color hex:', mb.colors[idx].hex);
  if (!hex?.trim()) {
    return;
  }
  mb.colors[idx].hex = hex.trim();
  renderBriefReview();
}

/** Prompt user to add a new color to the palette. */
function brAddColor() {
  const hex = prompt('New color hex (e.g. #3b82f6):');
  if (!hex?.trim()) {
    return;
  }
  const mb = ST.marketingBrief;
  if (!mb) {
    return;
  }
  if (!mb.colors) {
    mb.colors = [];
  }
  mb.colors.push({ hex: hex.trim(), name: 'Custom', role: 'accent' });
  renderBriefReview();
}

/** Remove an image from the brief at the given index. */
function brDelImage(idx) {
  const mb = ST.marketingBrief;
  if (!mb?.images) {
    return;
  }
  mb.images.splice(idx, 1);
  renderBriefReview();
}

/** Add uploaded images to the brief via FileReader. */
function brAddImages(files) {
  if (!files?.length) {
    return;
  }
  const mb = ST.marketingBrief;
  if (!mb) {
    return;
  }
  if (!mb.images) {
    mb.images = [];
  }
  Array.from(files).forEach(f => {
    const reader = new FileReader();
    reader.onload = e => {
      mb.images.push({ src: e.target.result, alt: f.name, category: 'other', selected: true });
      renderBriefReview();
    };
    reader.readAsDataURL(f);
  });
}

/** Prompt user to add a value to a fact category. */
function brAddFact(key) {
  const val = prompt('Add value:');
  if (!val?.trim()) {
    return;
  }
  const mb = ST.marketingBrief;
  if (!mb?.facts) {
    return;
  }
  const f = mb.facts;
  if (key === 'serviceArea' || key === 'yearsInBusiness') {
    f[key] = val.trim();
  } else if (key === 'services') {
    if (!f.services) {
      f.services = [];
    }
    f.services.push({ name: val.trim(), description: '', price: '' });
  } else if (key === 'namedStaff') {
    if (!f.namedStaff) {
      f.namedStaff = [];
    }
    f.namedStaff.push({ name: val.trim(), role: '' });
  } else if (key === 'testimonials') {
    if (!f.testimonials) {
      f.testimonials = [];
    }
    f.testimonials.push({ quote: val.trim(), author: '' });
  } else if (key === 'stats') {
    if (!f.stats) {
      f.stats = [];
    }
    f.stats.push({ number: val.trim(), label: '' });
  } else {
    if (!f[key]) {
      f[key] = [];
    }
    f[key].push(val.trim());
  }
  renderBriefReview();
}

/** Remove a fact value at the given index from the given category. */
function brRemoveFact(key, idx) {
  const mb = ST.marketingBrief;
  if (!mb?.facts) {
    return;
  }
  const f = mb.facts;
  if (key === 'serviceArea' || key === 'yearsInBusiness') {
    f[key] = '';
  } else if (Array.isArray(f[key])) {
    f[key].splice(idx, 1);
  }
  renderBriefReview();
}

/** Toggle inline editing for a conversion intelligence field. */
function brEditConv(key) {
  const mb = ST.marketingBrief;
  if (!mb) {
    return;
  }
  const el = $(`br-conv-${key}`);
  if (!el || el.querySelector('textarea')) {
    return;
  }

  // Array fields vs string fields
  const isArray = ['ctaTexts', 'proofPoints', 'objections', 'navLinks'].includes(key);
  const curVal = isArray
    ? (mb[key] || []).join(', ')
    : key === 'desiredAction'
      ? [mb.desiredAction, mb.actionDetail].filter(Boolean).join(' — ')
      : mb[key] || '';

  el.innerHTML = `<textarea class="br-edit-ta" style="min-height:40px">${esc(curVal)}</textarea><button class="btn btn-g btn-sm" style="margin-top:4px;float:right" onclick="G.brSaveConv('${key}')">Done</button>`;
  el.querySelector('textarea').focus();
}

/** Save an edited conversion intelligence field from its textarea. */
function brSaveConv(key) {
  const mb = ST.marketingBrief;
  if (!mb) {
    return;
  }
  const el = $(`br-conv-${key}`);
  const ta = el?.querySelector('textarea');
  if (!ta) {
    return;
  }
  const val = ta.value.trim();

  const isArray = ['ctaTexts', 'proofPoints', 'objections', 'navLinks'].includes(key);
  if (key === 'socialLinks') {
    // socialLinks is display-only for now, skip save
    renderBriefReview();
    return;
  }
  if (isArray) {
    mb[key] = val
      ? val
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
      : [];
  } else if (key === 'desiredAction') {
    const parts = val.split('—').map(s => s.trim());
    mb.desiredAction = parts[0] || '';
    mb.actionDetail = parts[1] || '';
  } else {
    mb[key] = val;
  }
  if (!mb.userEdits) {
    mb.userEdits = {};
  }
  mb.userEdits[key] = true;
  renderBriefReview();
}

/** Navigate forward from brief review — routes based on startPath. */
async function proceedFromBriefReview() {
  const path = ST.startPath;
  if (path === 'existing') {
    G.go('ex-detail');
    return;
  }

  // Capture inspiration screenshot from refsite URL if provided (Q&A and docs paths)
  const refsiteUrl = ST.brief?.refsite?.trim();
  if (refsiteUrl && /^https?:\/\//.test(refsiteUrl) && !ST.inspoImg) {
    const btn = $('br-proceed-btn');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Capturing inspiration\u2026';
    }
    const inspo = await captureInspoScreenshot(refsiteUrl);
    if (inspo) {
      ST.inspoImg = inspo;
    }
  }

  if (path === 'docs') {
    // Pass uploaded images to concept generation
    const imgs = ST.uploads.images.filter(i => i.analysis?.useDecision === 'use');
    requireKey(() => {
      genConcept('', ST.inspoImg, imgs.length ? imgs : []);
    });
  } else {
    // Q&A path → concept generation
    requireKey(() => {
      genConcept('', ST.inspoImg);
    });
  }
}

/** Navigate back from brief review — returns to the originating screen. */
function briefReviewBack() {
  const path = ST.startPath;
  if (path === 'existing') {
    G.go('existing');
  } else if (path === 'docs') {
    G.go('docview');
  } else {
    G.go('summary');
  }
}

/** Re-run the analysis for the current onboarding path to regenerate the brief. */
function regenerateBrief() {
  // Re-run the analysis for the current path
  const path = ST.startPath;
  ST.marketingBrief = null;
  if (path === 'existing') {
    analyseExisting();
  } else if (path === 'docs') {
    runDocAnalysis();
  } else {
    runQAEnrichment();
  }
}

// ── MODULE EXPORTS (activate when switching to ES modules) ──────
export {
  brAddColor,
  brAddFact,
  brAddImages,
  brAddTag,
  brDelImage,
  brEditColor,
  brEditConv,
  brEditField,
  briefReviewBack,
  brRemoveFact,
  brRemoveTag,
  brSaveConv,
  downloadBrief,
  getFactCategories,
  proceedFromBriefReview,
  regenerateBrief,
  renderBriefReview,
  renderFactInventory,
  setFactValues,
};
